
import { useQuery } from '@apollo/client';
import { useCallback, useRef, useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";

import {
  Radio,
  Dropdown,
  Search,
  Item,
  Divider,
  Icon,
  Container,
  Header,
  Segment,
} from 'semantic-ui-react'

import { ResponsiveContainer } from '../../../../components/ResponsiveContainer';

import { GET_TAGS as GET_POSSIBLE_TAGS } from '../../../../queries/tags';
import { GET_CLASSES } from '../data/GET_CLASSES';
import { GET_ORGANISATION_PERSON_ID_FORMULA } from '../data/GET_ORGANISATION_PERSON_ID_FORMULA';

// These will be different when the next block starts
const CURRENT_BLOCK_TAG_ID = process.env.NODE_ENV === 'development' ? 'tagId:113' : 'tagId:1774'

const AdminClassesView = () => {
  let navigate = useNavigate();
  const location = useLocation();

  const urlSearchParams = new URLSearchParams(location.search);
  const [search, setSearch] = useState(urlSearchParams.get('search') || "");
  const [tagIds, setTagIds] = useState<string[]>(urlSearchParams.get('tagIds') ? urlSearchParams.get('tagIds')!.split(',') : [CURRENT_BLOCK_TAG_ID]);
  // need to check for "tagId:113" in the tagIds array
  const [currentBlockOnly, setCurrentBlockOnly] = useState(tagIds.includes(CURRENT_BLOCK_TAG_ID));
  const { data: meData } = useQuery(GET_ORGANISATION_PERSON_ID_FORMULA);
  const [shouldShowSearch, setShouldShowSearch] = useState(urlSearchParams.get('search') !== null || urlSearchParams.get('tagIds') !== null);

  const { loading: classesLoading, error: classesError, data: classesData, refetch } = useQuery(GET_CLASSES,
    {
      variables: {
        search: search,
        filters: tagIds && tagIds.length > 0 ? tagIds.join(',') : currentBlockOnly ? CURRENT_BLOCK_TAG_ID : ""
      }
  });

  // check if there are more than 6 classes, if so, show the search bar
  useEffect(() => {
    if (classesData && classesData.classes && classesData.classes.length > 6) {
      setShouldShowSearch(true);
    }
  }, [classesData]);

  const { loading: tagsLoading, error: tagsError, data: tagsData} = useQuery(GET_POSSIBLE_TAGS, {
      variables: {
          showPeopleTags: false,
          showCourseTags: true,
      }
  });

  const setAndHoldTagIds = useCallback((ids: string[]) => {
    if (!ids.includes(CURRENT_BLOCK_TAG_ID)) {
      setCurrentBlockOnly(false);
    }
    setTagIds(ids);
    navigate({ pathname: location.pathname, search: `?search=${search}&tagIds=${ids.join(',')}` });
  },[search, navigate, location.pathname]);

  const timeoutRef = useRef();

  const handleSearchChange = useCallback((_: any, data: any) => {
    clearTimeout(timeoutRef.current)
    setSearch(data.value)

    // @ts-ignore
    timeoutRef.current = setTimeout(() => {
        navigate({ pathname: location.pathname, search: `?search=${data.value}&tagIds=${tagIds.join(',')}` });
        refetch({ search: data.value, filters: tagIds && tagIds.length > 0 ? tagIds.join(',') : currentBlockOnly ? CURRENT_BLOCK_TAG_ID : "" })
    }, 500);
  }, [tagIds, currentBlockOnly, navigate, location.pathname, refetch]);


  // On path change from broswer back button, we need to update the search state, add a 500ms debounce to the search change handler
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const searchParam = params.get('search');
    if (searchParam === null || searchParam === "") {
      // If no search param, reset search state
      setSearch("");
      if (!params.has('tagIds')) {
        // If no tagIds param, reset tagIds state to currentBlockOnly
        setTagIds([CURRENT_BLOCK_TAG_ID]);
        refetch({ search: "", filters: CURRENT_BLOCK_TAG_ID });
      } else {
        // If tagIds param exists, update tagIds state
        const tagIdsParam = params.get('tagIds') || "";
        const newTagIds = tagIdsParam.split(',').filter(id => id !== "");
        setTagIds(newTagIds);
        refetch({ search: "", filters: newTagIds && newTagIds.length > 0 ? newTagIds.join(',') : currentBlockOnly ? CURRENT_BLOCK_TAG_ID : "" });
      }
    } else {
      // If search param exists, update search state
      setSearch(searchParam);
      const tagIdsParam = params.get('tagIds') || "";
      const newTagIds = tagIdsParam.split(',').filter(id => id !== "");
      setTagIds(newTagIds);
      refetch({ search: searchParam, filters: newTagIds && newTagIds.length > 0 ? newTagIds.join(',') : currentBlockOnly ? CURRENT_BLOCK_TAG_ID : "" });
    }

  }, [location.search, refetch, currentBlockOnly]);

  const myClassCount =  !classesLoading && !classesError && classesData != null && classesData.classes.length > 0 && classesData.classes.filter((t:any) => {
                if (meData == null) return false
    return t.overseers != null && t.overseers.map((i:any) => i.name).includes(meData.me.name)
            }).length

  const hasOwnClasses = myClassCount > 0 && shouldShowSearch && myClassCount < classesData.classes.length

  return (
    <ResponsiveContainer>
        <Segment style={{ padding: '0em 0em' }} vertical>
        <Container>
            <Divider horizontal>
            <Header as='h2'>
                Classes
            </Header>
            </Divider>
            { shouldShowSearch && <Search
                loading={classesLoading}
                input={{ fluid: true }}
                size='large'
                placeholder='Search...'
                open={false}
                onSearchChange={handleSearchChange}
                value={search}
            />}
            {shouldShowSearch && (
            <>
            <br/>
            <Dropdown
                placeholder='Tags'
                fluid
                multiple
                value={tagIds}
                onChange={(_, { value }) => {
                setAndHoldTagIds(value as string[])
                refetch({ search: search, filters: value && (value as string[]).length > 0 ? (value as string[]).join(',') : currentBlockOnly ? CURRENT_BLOCK_TAG_ID : ""})
                }}
                search
                selection
                options={
                tagsLoading || tagsError ? [] :
                ((tagsData && tagsData.tags) || [])
                    .map((p: any) => ({
                    key: p.id,
                    text: p.name,
                    value: `tagId:${p.id}`,
                    }))
                }
            /></>)}
            {shouldShowSearch && (
            <>
            <br/>
            {/* ON be default */}
            <Radio
            toggle
            label='Current block classes only'
                checked={currentBlockOnly}
                onChange={(_, data:any) => {
                // if you toggle this then add the current block tag id to the tagIds if it is not already there
                setCurrentBlockOnly(data.checked)
                const filters = data.checked ? [...tagIds, CURRENT_BLOCK_TAG_ID] : [...tagIds.filter((t:any) => t !== CURRENT_BLOCK_TAG_ID)]
                setAndHoldTagIds(filters)
                refetch({ search: search, filters: filters && filters.length > 0 ? filters.join(',') : "" })
                }}
            />
            </>
            )}
            {hasOwnClasses && (<>
            <Divider horizontal>
            My classes
            </Divider>
            <Item.Group divided>
            { !classesLoading && !classesError && classesData != null && classesData.classes.length > 0 && classesData.classes.filter((t:any) => {
                if (meData == null) return false
                // TODO: change this for new block
                return t.overseers != null && t.overseers.map((i:any) => i.name).includes(meData.me.name) && t.SKU.endsWith('APR2026')
            }).map((t:any) => {

            return (
                <Item style={{cursor: 'pointer'}} key={t.id} onClick={() => navigate(`/classes/${t.id}`)}>
                <Item.Content>
                    <Item.Header>{t.name}</Item.Header>
                    <Item.Meta>{t.SKU}</Item.Meta>
                    <Icon name='group'/><b>Students:</b> {t.courseParticipantCount} | <Icon name='user'/> <b>Teachers:</b> {t && t.overseers.map((o: any) => (o.name)).join(', ')}
                </Item.Content>
                </Item>
            )
            })}
            </Item.Group>
            <Divider horizontal>
            All classes
            </Divider>
            </>)}
            <Item.Group divided>
            { !classesLoading && !classesError && classesData != null && classesData.classes.length > 0 ? classesData.classes.map((t:any) => (
            <Item style={{cursor: 'pointer'}} key={t.id} onClick={() => navigate(`/classes/${t.id}`)}>
                <Item.Content>
                <Item.Header>{t.name}</Item.Header>
                <Item.Meta>{t.SKU}</Item.Meta>
                <Icon name='group'/><b>Students:</b> {t.courseParticipantCount} | <Icon name='user'/> <b>Teachers:</b> {t && t.overseers.map((o: any) => (o.name)).join(', ')}
                </Item.Content>
            </Item>
            )) : (
            <>
                {new Array(20).fill(1).map((i) => (<br />))}
            </>
            )}
            <>
            {new Array(10).fill(1).map((i) => (<br key={i}/>))}
            </>
            </Item.Group>
        </Container>
        </Segment>
    </ResponsiveContainer>
  );
};

export { AdminClassesView }
